<template>
  <chart :options="graph" :auto-resize="true"></chart>
</template>

<script>
import EchartsTemplate from 'components/mixins/echarts-template';

import moment from 'moment';
import { each, get, has } from 'lodash';

import { CHART_COLORS } from 'constants/content-colors';

import 'echarts/lib/chart/line';

export default {
  mixins: [EchartsTemplate],
  data() {
    return {
      group: 'body_vitals',
      chart: 'chart-body-vitals-heart-respiratory-rates',
      labels: {
        close: this.$t('label.close'),
        refresh: this.$t('label.refresh'),
        dataView: this.$t('label.data_view'),
        loading: `${this.$t('label.loading')}...`,
        bloodPressureSystolic: this.$t('label.blood_pressure_systolic'),
        bloodPressureDiastolic: this.$t('label.blood_pressure_diastolic'),
        bloodGlucose: this.$t('label.blood_glucose'),
        bloodPressure: this.$t('label.blood_pressure'),
        saveAsImage: this.$t('label.save_as_image'),
        measurements: this.$t('label.measurements'),
        text: `${this.$t(this.chartDetails.section)}: ${this.$t(this.chartDetails.chart)}`,
        graphGeneratedByDietCreator: this.$t('label.graph_generated_by_diet_creator'),
        subtext: `${this.measurementDetails.participant}, ${this.$t('label.time_period')}: ${moment(this.measurementDetails.date[0]).format('DD/MM/YYYY')} - ${moment(this.measurementDetails.date[1]).format('DD/MM/YYYY')}`,
      },
    };
  },
  methods: {
    formatLabel(measurement, formatDate = 'DD/MM/YYYY', formatTime = 'HH:mm') {
      const timestamp = has(measurement, 'measurement_timestamp') ? measurement.measurement_timestamp : '';
      const date = moment(timestamp).format(formatDate);
      const time = moment(timestamp).format(formatTime);
      return `${date} ${time}`;
    },
    prepareData() {
      return new Promise((resolve) => {
        const series = [];

        const labels = [];

        const bloodPressureSystolic = {
          name: this.labels.bloodPressureSystolic,
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbolSize: 6,
          data: [],
        };

        const bloodPressureDiastolic = {
          name: this.labels.bloodPressureDiastolic,
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbolSize: 6,
          data: [],
        };

        const bloodGlucose = {
          name: this.labels.bloodGlucose,
          type: 'line',
          smooth: true,
          xAxisIndex: 1,
          yAxisIndex: 1,
          symbolSize: 6,
          data: [],
        };

        each(this.measurements, (measurement) => {
          const label = this.formatLabel(measurement, 'DD/MM');
          labels.push(label);

          const bloodPressureSystolicValue = get(measurement, 'MeasurementTypeGroups.body_vitals.Measurements.blood_pressure_systolic.value') || null;
          // const bloodPressureSystolicName = get(measurement, 'MeasurementTypeGroups.body_vitals.Measurements.blood_pressure_systolic.name') || null;
          const bloodPressureSystolicUnit = get(measurement, 'MeasurementTypeGroups.body_vitals.Measurements.blood_pressure_systolic.expressed_in_unit') || '';

          if (bloodPressureSystolicValue) {
            // this.labels.bloodPressureSystolic = bloodPressureSystolicName || this.labels.bloodPressureSystolic;
            bloodPressureSystolic.name = this.labels.bloodPressureSystolic;
            this.weightUnit = bloodPressureSystolicUnit;
            bloodPressureSystolic.data.push({
              name: this.labels.bloodPressureSystolic,
              value: bloodPressureSystolicValue,
              unit: bloodPressureSystolicUnit,
              seq: 2,
            });
          }

          const bloodPressureDiastolicValue = get(measurement, 'MeasurementTypeGroups.body_vitals.Measurements.blood_pressure_diastolic.value') || null;
          // const bloodPressureDiastolicName = get(measurement, 'MeasurementTypeGroups.body_vitals.Measurements.blood_pressure_diastolic.name') || null;
          const bloodPressureDiastolicUnit = get(measurement, 'MeasurementTypeGroups.body_vitals.Measurements.blood_pressure_diastolic.expressed_in_unit') || '';

          if (bloodPressureDiastolicValue) {
            // this.labels.bloodPressureDiastolic = bloodPressureDiastolicName || this.labels.bloodPressureDiastolic;
            bloodPressureDiastolic.name = this.labels.bloodPressureDiastolic;
            this.weightUnit = bloodPressureDiastolicUnit;
            bloodPressureDiastolic.data.push({
              name: this.labels.bloodPressureDiastolic,
              value: bloodPressureDiastolicValue,
              unit: bloodPressureDiastolicUnit,
              seq: 2,
            });
          }

          const bloodGlucoseValue = get(measurement, 'MeasurementTypeGroups.body_vitals.Measurements.blood_glucose.value') || null;
          const bloodGlucoseName = get(measurement, 'MeasurementTypeGroups.body_vitals.Measurements.blood_glucose.name') || null;
          const bloodGlucoseUnit = get(measurement, 'MeasurementTypeGroups.body_vitals.Measurements.blood_glucose.expressed_in_unit') || '';

          if (bloodGlucoseValue) {
            this.labels.bloodGlucose = bloodGlucoseName || this.labels.bloodGlucose;
            bloodGlucose.name = this.labels.bloodGlucose;
            this.weightUnit = bloodGlucoseUnit;
            bloodGlucose.data.push({
              name: this.labels.bloodGlucose,
              value: bloodGlucoseValue,
              unit: bloodGlucoseUnit,
              seq: 2,
            });
          }
        });

        if (bloodPressureSystolic.data.length > 0) series.push(bloodPressureSystolic);
        if (bloodPressureDiastolic.data.length > 0) series.push(bloodPressureDiastolic);

        if (bloodGlucose.data.length > 0) series.push(bloodGlucose);

        resolve({ series, labels });
      });
    },
    prepareGraph() {
      this.prepareData().then((data) => {
        const graph = {
          title: {
            text: this.labels.text,
            subtext: this.labels.subtext,
            subtextStyle: {
              color: '#000',
              fontSize: 13,
            },
            top: 10,
            left: '10%',
            padding: 0,
            itemGap: 5,
          },
          toolbox: {
            feature: {
              saveAsImage: {
                show: true,
                title: this.labels.saveAsImage,
              },
              dataView: {
                show: true,
                title: this.labels.dataView,
                lang: [this.labels.dataView, this.labels.close, this.labels.refresh],
                readOnly: true,
              },
            },
            top: 7,
            right: 15,
            itemGap: 15,
          },
          graphic: [
            {
              type: 'text',
              top: 'center',
              right: '20',
              rotation: Math.PI / 2,
              style: {
                text: this.labels.graphGeneratedByDietCreator,
                fill: '#afafaf',
                textVeticalAlign: 'middle',
              },
            },
          ],
          tooltip: {
            trigger: 'axis',
            formatter: this.formatTooltip,
          },
          legend: {
            align: 'right',
            top: 10,
            right: '10%',
            data: [
              { name: this.labels.bloodPressureSystolic },
              { name: this.labels.bloodPressureDiastolic },
              { name: this.labels.bloodGlucose },
            ],
          },
          color: [
            CHART_COLORS.systolic,
            CHART_COLORS.distolic,
            CHART_COLORS.glucose,
          ],
          grid: [
            {
              left: 50,
              right: 100,
              height: '32.5%',
            },
            {
              left: 50,
              right: 100,
              top: '60%',
              height: '32.5%',
            },
          ],
          xAxis: [
            {
              type: 'category',
              data: data.labels,
              boundaryGap: false,
              axisLine: { onZero: true },
              name: this.labels.measurements,
              nameLocation: 'middle',
              nameGap: 25,
              nameTextStyle: {
                fontWeight: 'bold',
              },
              splitLine: {
                show: true,
                lineStyle: {
                  type: 'dashed',
                },
              },
            },
            {
              gridIndex: 1,
              position: 'top',
              type: 'category',
              data: data.labels,
              boundaryGap: false,
              axisLine: { onZero: true },
            },
          ],
          yAxis: [
            {
              type: 'value',
              gridIndex: 0,
              name: this.labels.bloodPressure,
            },
            {
              gridIndex: 1,
              name: this.labels.bloodGlucose,
              type: 'value',
              nameGap: 25,
              // inverse: true,
            },
          ],
          series: data.series,
        };
        this.graph = graph;
      });
    },
  },
};
</script>

<style>
.echarts {
  width: 100% !important;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/professional/persons/participants/participant-evolution/dashboard/charts/chart-body-vitals-blood-pressure-glucose.vue